/**
 * Creating a child event from an event's detail page: a copy of the parent's settings, placed
 * beneath the parent in the campaign hierarchy (a LIFE_CYCLE_EVENT node under the parent's node —
 * the same relationship ListSubEvents reports, see hierarchySync).
 *
 * What is copied is everything CreateEvent takes, with three exceptions:
 *
 *   - `eventName` gets {@link CHILD_NAME_SUFFIX}, so the clone is told apart on the calendar until
 *     the marketer renames it.
 *   - `sfdcCampaignId` is left empty. The service accepts only a real SFDC id (exactly 18
 *     alphanumerics, AWSECEventHubModel `SfdcCampaignId`), so a "<parent id> - to be updated"
 *     marker would be a 400. Copying the parent's id unchanged would be worse: two events would share
 *     one campaign and their attendee lists would merge. An event node needs no campaign id (the
 *     service derives it from the event, and accepts none), so the placement still works.
 *   - `description` is not copied. Its images and file links are uploads stored under the parent's
 *     event id (`<img data-file-id>`, AWSECEventHub EventDescriptionHtml), which the service resolves
 *     against the event being read — on the child they would point at nothing.
 *
 * Owner, status and lifecycle are not copied either: the creator owns the clone, and CreateEvent
 * always makes a fresh DRAFT.
 *
 * Two calls, not one transaction: the event is created first, then placed. If the placement fails
 * the event still exists, so the result carries it with the error instead of throwing — the page
 * says what happened and the marketer can add it to the hierarchy on Edit Event.
 */

import type { CreateEventInput, Event, EventCalendarClient } from '../client/EventCalendarClient';

export const CHILD_NAME_SUFFIX = ' - child clone';

/** The CreateEvent input for a child of `parent`, owned by `owner` (see the header for what is copied). */
export function childEventInput(parent: Event, owner: string): CreateEventInput {
  return {
    eventName: `${parent.eventName ?? ''}${CHILD_NAME_SUFFIX}`,
    eventOwner: owner,
    eventType: parent.eventType,
    eventSubType: parent.eventSubType,
    businessUnits: parent.businessUnits ? [...parent.businessUnits] : undefined,
    segment: parent.segment,
    province: parent.province,
    city: parent.city,
    startDate: parent.startDate,
    endDate: parent.endDate,
    bdNominationSupported: parent.bdNominationSupported,
    nominationStartDate: parent.nominationStartDate,
    nominationEndDate: parent.nominationEndDate,
    nominationForm: parent.nominationForm ? [...parent.nominationForm] : undefined,
    overallTarget: parent.overallTarget,
    byBusinessUnitTargets: parent.byBusinessUnitTargets?.map((t) => ({ ...t })),
    clientToken: crypto.randomUUID(),
  };
}

export interface ChildEventResult {
  /** The created child. */
  event: Event;
  /** Why it could not be placed under the parent, if it could not. The event exists regardless. */
  placementError?: string;
}

/**
 * Creates the child and places it under `parent` in the hierarchy. A parent with no node yet gets
 * one first, as a root — what saving its hierarchy on Edit Event would do. Rejects only when the
 * event itself could not be created.
 *
 * `parentNodeId` is the parent's node when the caller already knows it (the detail page has looked
 * it up for its hierarchy): a string to place under, `null` for "the parent has no node", undefined
 * to look it up here (ListCampaignRoots?eventId=).
 */
export async function createChildEvent(
  client: Pick<EventCalendarClient, 'createEvent' | 'campaignHierarchy'>,
  parent: Event,
  owner: string,
  parentNodeId?: string | null,
): Promise<ChildEventResult> {
  const event = await client.createEvent(childEventInput(parent, owner));
  try {
    if (!parent.eventId || !event.eventId) throw new Error('missing event id');
    const api = client.campaignHierarchy();
    const known = parentNodeId === undefined ? (await api.findEventNode(parent.eventId))?.nodeId : parentNodeId;
    const underNodeId = known ?? (await api.createEventNode(parent.eventId));
    await api.createEventNode(event.eventId, underNodeId);
    return { event };
  } catch (e: unknown) {
    return { event, placementError: e instanceof Error ? e.message : String(e) };
  }
}
